import React, { useEffect, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { toast } from "react-toastify";
import {
  FaTrash,
  FaPlus,
  FaVideo,
  FaImage,
  FaCloudUploadAlt,
  FaArrowLeft,
  FaArrowRight,
  FaFilter,
} from "react-icons/fa";
import { MdEdit } from "react-icons/md";
import { IoClose } from "react-icons/io5";
import { GiSparkles } from "react-icons/gi";
import { motion } from "framer-motion";
import { Container, Dialog, DialogContent, DialogTitle } from "@mui/material";
import { server } from "../server";
import { uploadMediaToServer } from "../utils/uploadMediaToServer";
import Loading from "../Components/global/Loading";
import {
  getAllCategoriesList,
  getAllSubCategoriesList,
  getAllGalleryMedias,
} from "../redux/actions/contents";
import { getYoutubeEmbedUrl } from "../utils/youTube";
import { deleteMediaFromServer } from "../utils/deleteMediaFromServer";
import PageName from "../Components/global/PageName";
import compressImage from "../utils/compressAsset";

export default function GalleryManager() {
  const dispatch = useDispatch();
  const {
    galleryList,
    categoriesList,
    subCategoriesList,
    loading: galleryLoading,
  } = useSelector((state) => state?.content);

  const { register, handleSubmit, watch, reset, setValue } = useForm({
    defaultValues: {
      mediaType: "image",
      categoryId: "",
      subCategoryId: "",
    },
  });

  const [previews, setPreviews] = useState([]);
  const [uploadProgress, setUploadProgress] = useState(null);
  const mediaType = watch("mediaType");
  const selectedCategoryId = watch("categoryId");
  const [loading, setLoading] = useState(false);
  const [sync, setSync] = useState(0);

  // Edit Modal State
  const [editItem, setEditItem] = useState(null);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editCategoryId, setEditCategoryId] = useState("");
  const [editSubCategoryId, setEditSubCategoryId] = useState("");
  const [editCaption, setEditCaption] = useState("");
  const [editMediaUrl, setEditMediaUrl] = useState("");
  const [editMediaType, setEditMediaType] = useState("image");

  // Media Filter, Category Filter, & SubCategory Filter States
  const [filterMediaType, setFilterMediaType] = useState("all"); // 'all' | 'image' | 'video'
  const [filterCategory, setFilterCategory] = useState("all");
  const [filterSubCategory, setFilterSubCategory] = useState("all");

  // Pagination states
  const [adminPageNo, setAdminPageNo] = useState(1);
  const ADMIN_ITEMS_PER_PAGE = 6;

  useEffect(() => {
    dispatch(getAllGalleryMedias(1, 100));
    dispatch(getAllCategoriesList(1, 100));
    dispatch(getAllSubCategoriesList(1, 100));
  }, [dispatch, sync]);

  // Filtered SubCategories for Add Form
  const availableSubCategoriesForAdd = useMemo(() => {
    if (!subCategoriesList || !selectedCategoryId) return [];
    return subCategoriesList.filter(
      (sub) => String(sub.categoryId) === String(selectedCategoryId)
    );
  }, [subCategoriesList, selectedCategoryId]);

  // Filtered SubCategories for Edit Modal
  const availableSubCategoriesForEdit = useMemo(() => {
    if (!subCategoriesList || !editCategoryId) return [];
    return subCategoriesList.filter(
      (sub) => String(sub.categoryId) === String(editCategoryId)
    );
  }, [subCategoriesList, editCategoryId]);

  // Filtered SubCategories for Filter Bar
  const availableSubCategoriesForFilter = useMemo(() => {
    if (!subCategoriesList) return [];
    if (filterCategory === "all") return subCategoriesList;
    return subCategoriesList.filter(
      (sub) => String(sub.categoryId) === String(filterCategory)
    );
  }, [subCategoriesList, filterCategory]);

  // Filtered Gallery List based on Media Type, Category, & SubCategory Filters
  const filteredGalleryList = useMemo(() => {
    if (!galleryList) return [];
    return galleryList.filter((item) => {
      const typeMatch = filterMediaType === "all" || item.mediaType === filterMediaType;
      const catMatch = filterCategory === "all" || String(item.categoryId) === String(filterCategory);
      const subCatMatch =
        filterSubCategory === "all" || String(item.subCategoryId) === String(filterSubCategory);
      return typeMatch && catMatch && subCatMatch;
    });
  }, [galleryList, filterMediaType, filterCategory, filterSubCategory]);

  // Compute Total Pages & Slice for Admin
  const totalAdminPages = Math.ceil(filteredGalleryList.length / ADMIN_ITEMS_PER_PAGE) || 1;

  const currentAdminSlice = useMemo(() => {
    const startIdx = (adminPageNo - 1) * ADMIN_ITEMS_PER_PAGE;
    return filteredGalleryList.slice(startIdx, startIdx + ADMIN_ITEMS_PER_PAGE);
  }, [filteredGalleryList, adminPageNo]);

  // Handle filter changes
  const handleMediaFilterChange = (type) => {
    setFilterMediaType(type);
    setAdminPageNo(1);
  };

  const handleCategoryFilterChange = (catId) => {
    setFilterCategory(catId);
    setFilterSubCategory("all");
    setAdminPageNo(1);
  };

  const handleSubCategoryFilterChange = (subCatId) => {
    setFilterSubCategory(subCatId);
    setAdminPageNo(1);
  };

  const onSubmit = async (data) => {
    try {
      if (!data?.subCategoryId) {
        toast.error("Please select a SubCategory before adding an asset.");
        return;
      }

      setLoading(true);

      if (data?.mediaType === "image") {
        const files = Array.from(data?.media || []);
        if (!files.length) {
          toast.error("Please select at least one image file to upload.");
          setLoading(false);
          return;
        }

        let successCount = 0;
        const totalFiles = files.length;
        setUploadProgress({ current: 0, total: totalFiles });

        for (let i = 0; i < totalFiles; i++) {
          const file = files[i];
          const compressed = await compressImage(file);
          const mediaUrl = await uploadMediaToServer(compressed, "null");

          await axios.post(
            `${server}/gallery/new`,
            null,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
              },
              params: {
                categoryId: data.categoryId,
                subCategoryId: data.subCategoryId || null,
                mediaType: "image",
                caption: data.caption,
                mediaUrl,
              },
            }
          );
          successCount++;
          setUploadProgress({ current: successCount, total: totalFiles });
        }

        toast.success(
          successCount === 1
            ? "Gallery item uploaded successfully!"
            : `${successCount} gallery items uploaded successfully!`
        );
      } else {
        const mediaUrl = data.mediaUrl;
        const { data: resData } = await axios.post(
          `${server}/gallery/new`,
          null,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
            params: {
              categoryId: data.categoryId,
              subCategoryId: data.subCategoryId || null,
              mediaType: data.mediaType,
              caption: data.caption,
              mediaUrl,
            },
          }
        );

        toast.success(resData?.message || "Gallery item uploaded successfully!");
      }

      reset({
        mediaType: data.mediaType,
        categoryId: data.categoryId,
        subCategoryId: "",
        mediaUrl: "",
        caption: "",
      });
      setPreviews([]);
      setSync((prev) => prev + 1);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message);
    } finally {
      setLoading(false);
      setUploadProgress(null);
    }
  };

  const handleDelete = async (id, imageUrl) => {
    const userRes = window.confirm(
      "Are you sure you want to delete this gallery artwork?"
    );
    if (!userRes) return;

    try {
      setLoading(true);
      if (imageUrl) await deleteMediaFromServer(imageUrl);
      await axios.delete(`${server}/gallery/deleteById`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          mediaId: id,
        },
      });
      toast.success("Gallery item deleted.");
      setSync((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEditModal = (item) => {
    setEditItem(item);
    setEditCategoryId(item?.categoryId || "");
    setEditSubCategoryId(item?.subCategoryId || "");
    setEditCaption(item?.caption || "");
    setEditMediaUrl(item?.mediaUrl || "");
    setEditMediaType(item?.mediaType || "image");
    setEditModalOpen(true);
  };

  const handleUpdateGalleryItem = async (e) => {
    if (e) e.preventDefault();

    if (!editSubCategoryId) {
      toast.error("Please select a SubCategory.");
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.put(`${server}/gallery/updateById`, null, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        params: {
          mediaId: editItem?.id,
          categoryId: editCategoryId,
          subCategoryId: editSubCategoryId || 0,
          caption: editCaption,
          mediaUrl: editMediaUrl,
          mediaType: editMediaType,
        },
      });

      toast.success(data?.message || "Gallery item updated successfully!");
      setEditModalOpen(false);
      setEditItem(null);
      setSync((prev) => prev + 1);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen pb-16 bg-[#0A0A0C] text-[#F8F5F2]">
      <PageName
        title="Gallery Manager"
        subTitle="Upload and filter portfolio high-res photos, videos, categories, and subcategories."
      />

      <div className="max-w-7xl mx-auto px-4 mt-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Upload Form Card */}
          <div className="lg:col-span-5 xl:col-span-4">
            <div className="bg-[#121215]/90 backdrop-blur-2xl p-6 md:p-7 rounded-3xl border border-[#C8A165]/25 shadow-[0_15px_40px_rgba(0,0,0,0.7)] sticky top-24 text-left">
              <div className="flex items-center gap-2 mb-1 text-[#C8A165]">
                <GiSparkles size={18} />
                <h3 className="text-base font-bold text-[#F8F5F2]">
                  Add Portfolio Artwork
                </h3>
              </div>
              <p className="text-xs text-[#F8F5F2]/50 mb-6">
                Categorize your work with Category and SubCategory tags.
              </p>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* Category Selection */}
                <div>
                  <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                    Select Category <span className="text-red-400">*</span>
                  </label>
                  <select
                    {...register("categoryId", {
                      required: true,
                      onChange: () => setValue("subCategoryId", ""),
                    })}
                    className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] transition-all cursor-pointer"
                  >
                    <option value="" className="bg-[#18181D]">Select Category</option>
                    {categoriesList?.map((e, i) => (
                      <option value={e?.id} key={i} className="bg-[#18181D]">
                        {e?.category}
                      </option>
                    ))}
                  </select>
                </div>

                {/* SubCategory Selection */}
                <div>
                  <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                    Select SubCategory <span className="text-red-400">*</span>
                  </label>
                  <select
                    {...register("subCategoryId", { required: true })}
                    disabled={!selectedCategoryId}
                    className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] transition-all cursor-pointer disabled:opacity-40"
                  >
                    <option value="" className="bg-[#18181D]">
                      {!selectedCategoryId
                        ? "Select Category First"
                        : availableSubCategoriesForAdd.length === 0
                        ? "No SubCategories for this Category"
                        : "-- Select SubCategory --"}
                    </option>
                    {availableSubCategoriesForAdd?.map((sub) => (
                      <option value={sub?.id} key={sub?.id} className="bg-[#18181D]">
                        {sub?.subCategory}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Media Type Selection (Radio Buttons) */}
                <div>
                  <label className="block text-xs font-semibold text-[#C8A165] mb-2">
                    Media Type
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    <label
                      className={`flex items-center gap-2.5 p-3 rounded-2xl border cursor-pointer transition-all ${
                        mediaType === "image"
                          ? "bg-[#C8A165]/15 border-[#C8A165] text-[#C8A165]"
                          : "bg-[#18181D] border-[#C8A165]/20 text-[#F8F5F2]/70 hover:border-[#C8A165]/50 hover:text-[#F8F5F2]"
                      }`}
                    >
                      <input
                        type="radio"
                        value="image"
                        {...register("mediaType", { required: true })}
                        className="accent-[#C8A165] w-4 h-4 cursor-pointer"
                      />
                      <span className="text-xs font-semibold flex items-center gap-1.5">
                        <FaImage size={14} /> Image Photo
                      </span>
                    </label>

                    <label
                      className={`flex items-center gap-2.5 p-3 rounded-2xl border cursor-pointer transition-all ${
                        mediaType === "video"
                          ? "bg-[#C8A165]/15 border-[#C8A165] text-[#C8A165]"
                          : "bg-[#18181D] border-[#C8A165]/20 text-[#F8F5F2]/70 hover:border-[#C8A165]/50 hover:text-[#F8F5F2]"
                      }`}
                    >
                      <input
                        type="radio"
                        value="video"
                        {...register("mediaType", { required: true })}
                        className="accent-[#C8A165] w-4 h-4 cursor-pointer"
                      />
                      <span className="text-xs font-semibold flex items-center gap-1.5">
                        <FaVideo size={14} /> Video Reel
                      </span>
                    </label>
                  </div>
                </div>

                {/* File Upload for Image (Multiple Selectable) */}
                {mediaType === "image" && (
                  <div>
                    <label className="block text-xs font-semibold text-[#C8A165] mb-1.5 flex items-center justify-between">
                      <span className="flex items-center gap-1">
                        <FaCloudUploadAlt /> Upload Photo Image(s)
                      </span>
                      <span className="text-[0.65rem] text-[#F8F5F2]/50 font-normal">
                        Select single or multiple
                      </span>
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      {...register("media", { required: previews.length === 0 })}
                      className="w-full text-xs p-2.5 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] transition-all file:mr-3 file:py-1 file:px-3 file:rounded-xl file:border-0 file:text-[0.65rem] file:font-semibold file:bg-[#C8A165]/20 file:text-[#C8A165]"
                      onChange={(e) => {
                        if (e.target.files && e.target.files.length > 0) {
                          const fileArray = Array.from(e.target.files);
                          const newPreviews = fileArray.map((file) =>
                            URL.createObjectURL(file)
                          );
                          setPreviews(newPreviews);
                        } else {
                          setPreviews([]);
                        }
                      }}
                    />
                    {previews.length > 0 && (
                      <div className="mt-3">
                        <div className="flex items-center justify-between mb-1.5">
                          <span className="text-[0.68rem] text-[#C8A165] font-semibold">
                            Selected Images ({previews.length})
                          </span>
                          <button
                            type="button"
                            onClick={() => setPreviews([])}
                            className="text-[0.65rem] text-red-400 hover:underline cursor-pointer"
                          >
                            Clear Selection
                          </button>
                        </div>
                        <div className="grid grid-cols-3 gap-2 max-h-48 overflow-y-auto p-2 bg-[#18181D] rounded-2xl border border-[#C8A165]/20">
                          {previews.map((src, idx) => (
                            <div
                              key={idx}
                              className="relative aspect-square rounded-xl overflow-hidden border border-[#C8A165]/30 group"
                            >
                              <img
                                src={src}
                                alt={`Preview ${idx + 1}`}
                                className="w-full h-full object-cover"
                              />
                              <span className="absolute bottom-1 right-1 bg-[#0A0A0C]/80 text-[#C8A165] text-[0.55rem] font-bold px-1.5 py-0.5 rounded">
                                #{idx + 1}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* URL Input for Video */}
                {mediaType === "video" && (
                  <div>
                    <label className="block text-xs font-semibold text-[#C8A165] mb-1.5 flex items-center gap-1">
                      <FaVideo /> YouTube Video Link
                    </label>
                    <input
                      {...register("mediaUrl", { required: true })}
                      className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] placeholder:text-[#F8F5F2]/30 transition-all"
                      placeholder="https://www.youtube.com/watch?v=..."
                    />
                  </div>
                )}

                {/* Caption */}
                <div>
                  <label className="block text-xs font-semibold text-[#F8F5F2]/80 mb-1.5">
                    Caption / Title
                  </label>
                  <input
                    {...register("caption")}
                    className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] placeholder:text-[#F8F5F2]/30 transition-all"
                    placeholder="Enter caption or artwork name..."
                  />
                </div>

                {loading && uploadProgress && (
                  <div className="mt-3 p-3 bg-[#18181D] border border-[#C8A165]/40 rounded-2xl">
                    <div className="flex justify-between items-center text-xs font-semibold text-[#C8A165] mb-1.5">
                      <span>Uploading Progress</span>
                      <span>
                        {uploadProgress.current} out of {uploadProgress.total} completed
                      </span>
                    </div>
                    <div className="w-full bg-[#0A0A0C] h-2 rounded-full overflow-hidden border border-[#C8A165]/20">
                      <div
                        className="bg-gradient-to-r from-[#C8A165] to-[#F5E5C9] h-full transition-all duration-300"
                        style={{
                          width: `${Math.round(
                            (uploadProgress.current / uploadProgress.total) * 100
                          )}%`,
                        }}
                      />
                    </div>
                  </div>
                )}

                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.97 }}
                  className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl font-bold text-xs tracking-widest uppercase shadow-lg text-[#0A0A0C] transition-all disabled:opacity-50 mt-4 cursor-pointer"
                  style={{
                    background: "linear-gradient(135deg, #C8A165 0%, #F5E5C9 50%, #9E7A48 100%)",
                  }}
                >
                  {loading && uploadProgress ? (
                    `Uploading (${uploadProgress.current} out of ${uploadProgress.total} completed)`
                  ) : loading ? (
                    "Uploading..."
                  ) : (
                    <>
                      <FaPlus size={12} /> Add to Gallery
                    </>
                  )}
                </motion.button>
              </form>
            </div>
          </div>

          {/* Gallery Media Grid & Filters */}
          <div className="lg:col-span-7 xl:col-span-8">
            <div className="bg-[#121215]/90 backdrop-blur-2xl p-6 md:p-7 rounded-3xl border border-[#C8A165]/25 shadow-[0_15px_40px_rgba(0,0,0,0.7)] text-left flex flex-col justify-between min-h-[550px]">
              <div>
                {/* Header */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 pb-4 border-b border-[#C8A165]/15">
                  <div>
                    <h3 className="text-base font-bold text-[#F8F5F2] flex items-center gap-2">
                      <FaImage className="text-[#C8A165]" /> Gallery Artworks
                    </h3>
                    <p className="text-xs text-[#F8F5F2]/50 mt-0.5">
                      Showing {filteredGalleryList.length} of {galleryList?.length || 0} total media
                    </p>
                  </div>
                  <span className="bg-[#C8A165]/15 text-[#C8A165] border border-[#C8A165]/30 px-3 py-1 rounded-full font-bold text-xs tracking-wider">
                    Filtered: {filteredGalleryList.length}
                  </span>
                </div>

                {/* ══════════════════════════════════════════
                    MEDIA FILTER CONTROL BAR
                   ══════════════════════════════════════════ */}
                <div className="flex flex-wrap items-center justify-between gap-3 mb-6 bg-[#16161C] p-3 rounded-2xl border border-[#C8A165]/15">
                  {/* Media Type Filter Tabs */}
                  <div className="flex items-center gap-1.5">
                    <span className="text-[0.68rem] uppercase font-bold text-[#C8A165] flex items-center gap-1 mr-1">
                      <FaFilter size={10} /> Type:
                    </span>
                    {[
                      { id: "all", label: "All" },
                      { id: "image", label: "Photos" },
                      { id: "video", label: "Videos" },
                    ].map((tab) => (
                      <button
                        key={tab.id}
                        onClick={() => handleMediaFilterChange(tab.id)}
                        className={`px-2.5 py-1 rounded-xl text-xs font-semibold transition-all cursor-pointer ${
                          filterMediaType === tab.id
                            ? "bg-[#C8A165] text-[#0A0A0C] font-bold shadow-md"
                            : "text-[#F8F5F2]/60 hover:text-[#C8A165] hover:bg-white/5"
                        }`}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </div>

                  {/* Category & SubCategory Filter Dropdowns */}
                  <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                    {/* Category Filter */}
                    <div className="flex items-center gap-1">
                      <span className="text-[0.65rem] uppercase font-bold text-[#C8A165]">Cat:</span>
                      <select
                        value={filterCategory}
                        onChange={(e) => handleCategoryFilterChange(e.target.value)}
                        className="text-xs px-2.5 py-1 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-xl focus:outline-none focus:border-[#C8A165] cursor-pointer"
                      >
                        <option value="all">All Categories</option>
                        {categoriesList?.map((cat) => (
                          <option value={cat.id} key={cat.id}>
                            {cat.category}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* SubCategory Filter */}
                    <div className="flex items-center gap-1">
                      <span className="text-[0.65rem] uppercase font-bold text-[#C8A165]">SubCat:</span>
                      <select
                        value={filterSubCategory}
                        onChange={(e) => handleSubCategoryFilterChange(e.target.value)}
                        className="text-xs px-2.5 py-1 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-xl focus:outline-none focus:border-[#C8A165] cursor-pointer"
                      >
                        <option value="all">All SubCategories</option>
                        {availableSubCategoriesForFilter?.map((sub) => (
                          <option value={sub.id} key={sub.id}>
                            {sub.subCategory}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                {filteredGalleryList.length === 0 ? (
                  <div className="text-center py-16 text-[#F8F5F2]/40 text-xs bg-[#16161C] rounded-2xl border border-dashed border-[#C8A165]/20 p-8">
                    No gallery items match the selected media, category, or subcategory filter.
                  </div>
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
                    {currentAdminSlice?.map((item, index) => (
                      <motion.div
                        key={item?.id || item?._id || index}
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.04 }}
                        className="bg-[#16161C] border border-[#C8A165]/20 rounded-2xl overflow-hidden relative group hover:border-[#C8A165]/60 hover:shadow-[0_10px_30px_rgba(200,161,101,0.15)] transition-all flex flex-col justify-between"
                      >
                        {/* Action Buttons: Edit & Delete */}
                        <div className="absolute top-2.5 right-2.5 flex items-center gap-1.5 z-10 opacity-0 group-hover:opacity-100 transition-all">
                          <button
                            className="bg-[#18181D]/90 hover:bg-[#C8A165] border border-[#C8A165]/40 text-[#C8A165] hover:text-[#0A0A0C] p-2 rounded-full shadow-lg cursor-pointer transition-colors"
                            onClick={() => handleOpenEditModal(item)}
                            title="Edit Media"
                          >
                            <MdEdit size={12} />
                          </button>
                          <button
                            className="bg-red-500/20 hover:bg-red-600 border border-red-500/40 text-red-300 hover:text-white p-2 rounded-full shadow-lg cursor-pointer transition-colors"
                            onClick={() => handleDelete(item?.id, item?.mediaUrl)}
                            title="Delete Media"
                          >
                            <FaTrash size={11} />
                          </button>
                        </div>

                        {item.mediaType === "video" ? (
                          <div className="relative aspect-video bg-[#0A0A0C] flex items-center justify-center">
                            <iframe
                              src={getYoutubeEmbedUrl(item?.mediaUrl)}
                              className="w-full h-full border-0 pointer-events-none"
                              title={item.caption || "YouTube Reel"}
                            />
                            <div className="absolute top-2 left-2 bg-[#0A0A0C]/80 border border-[#C8A165]/40 text-[#C8A165] p-1.5 rounded-lg">
                              <FaVideo size={11} />
                            </div>
                          </div>
                        ) : (
                          <div className="relative aspect-square overflow-hidden bg-[#0A0A0C]">
                            <img
                              src={item.mediaUrl}
                              alt={item.caption || "Gallery"}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            />
                          </div>
                        )}

                        <div className="p-3.5 bg-[#121215] border-t border-[#C8A165]/15">
                          <p className="text-xs font-bold text-[#F8F5F2] truncate">
                            {item.caption || "Untitled Artwork"}
                          </p>
                          <div className="flex items-center gap-1 flex-wrap mt-1">
                            <span className="text-[0.62rem] text-[#C8A165] uppercase font-semibold tracking-wider bg-[#C8A165]/10 border border-[#C8A165]/20 px-2 py-0.5 rounded-md truncate">
                              {item.categoryName || "Bridal Gallery"}
                            </span>
                            {item.subCategoryName && (
                              <span className="text-[0.62rem] text-[#F8F5F2]/70 uppercase font-medium tracking-wider bg-white/5 border border-white/10 px-2 py-0.5 rounded-md truncate">
                                {item.subCategoryName}
                              </span>
                            )}
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>

              {/* Admin Gallery Pagination Bar */}
              {totalAdminPages > 1 && (
                <div className="flex items-center justify-between gap-4 mt-8 pt-4 border-t border-[#C8A165]/15">
                  <span className="text-xs text-[#F8F5F2]/50">
                    Page <strong className="text-[#C8A165]">{adminPageNo}</strong> of{" "}
                    <strong className="text-[#C8A165]">{totalAdminPages}</strong>
                  </span>

                  <div className="flex items-center gap-2">
                    <button
                      disabled={adminPageNo === 1}
                      onClick={() => setAdminPageNo((prev) => Math.max(prev - 1, 1))}
                      className="px-3 py-1.5 rounded-xl border border-[#C8A165]/30 text-xs font-bold text-[#C8A165] hover:bg-[#C8A165] hover:text-[#0A0A0C] disabled:opacity-30 disabled:pointer-events-none transition-all flex items-center gap-1 cursor-pointer"
                    >
                      <FaArrowLeft size={10} /> Prev
                    </button>

                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalAdminPages }, (_, i) => i + 1).map((pNum) => (
                        <button
                          key={pNum}
                          onClick={() => setAdminPageNo(pNum)}
                          className={`w-7 h-7 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                            adminPageNo === pNum
                              ? "bg-[#C8A165] text-[#0A0A0C]"
                              : "bg-[#18181D] text-[#F8F5F2]/70 hover:text-[#C8A165] border border-[#C8A165]/20"
                          }`}
                        >
                          {pNum}
                        </button>
                      ))}
                    </div>

                    <button
                      disabled={adminPageNo === totalAdminPages}
                      onClick={() => setAdminPageNo((prev) => Math.min(prev + 1, totalAdminPages))}
                      className="px-3 py-1.5 rounded-xl border border-[#C8A165]/30 text-xs font-bold text-[#C8A165] hover:bg-[#C8A165] hover:text-[#0A0A0C] disabled:opacity-30 disabled:pointer-events-none transition-all flex items-center gap-1 cursor-pointer"
                    >
                      Next <FaArrowRight size={10} />
                    </button>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>

      {/* Edit Gallery Item Modal */}
      <Dialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        PaperProps={{
          style: {
            backgroundColor: "#121215",
            color: "#F8F5F2",
            borderRadius: "1.5rem",
            border: "1px solid rgba(200, 161, 101, 0.3)",
            maxWidth: "500px",
            width: "100%",
          },
        }}
      >
        <DialogTitle className="flex justify-between items-center border-b border-[#C8A165]/20 pb-3">
          <span className="text-base font-bold text-[#F8F5F2] flex items-center gap-2">
            <MdEdit className="text-[#C8A165]" /> Edit Gallery Artwork
          </span>
          <button
            onClick={() => setEditModalOpen(false)}
            className="text-[#F8F5F2]/50 hover:text-[#C8A165] p-1 rounded-lg transition-colors cursor-pointer"
          >
            <IoClose size={20} />
          </button>
        </DialogTitle>
        <DialogContent className="pt-4">
          <form onSubmit={handleUpdateGalleryItem} className="space-y-4 mt-2">
            {/* Category Dropdown */}
            <div>
              <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                Category <span className="text-red-400">*</span>
              </label>
              <select
                value={editCategoryId}
                onChange={(e) => {
                  setEditCategoryId(e.target.value);
                  setEditSubCategoryId("");
                }}
                className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] transition-all cursor-pointer"
              >
                <option value="">Select Category</option>
                {categoriesList?.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.category}
                  </option>
                ))}
              </select>
            </div>

            {/* SubCategory Dropdown */}
            <div>
              <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                SubCategory <span className="text-red-400">*</span>
              </label>
              <select
                value={editSubCategoryId}
                onChange={(e) => setEditSubCategoryId(e.target.value)}
                disabled={!editCategoryId}
                className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] transition-all cursor-pointer disabled:opacity-40"
              >
                <option value="">
                  {!editCategoryId
                    ? "Select Category First"
                    : availableSubCategoriesForEdit.length === 0
                    ? "No SubCategories for this Category"
                    : "-- Select SubCategory --"}
                </option>
                {availableSubCategoriesForEdit?.map((sub) => (
                  <option key={sub.id} value={sub.id}>
                    {sub.subCategory}
                  </option>
                ))}
              </select>
            </div>

            {/* Caption */}
            <div>
              <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                Caption / Title
              </label>
              <input
                type="text"
                value={editCaption}
                onChange={(e) => setEditCaption(e.target.value)}
                className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] transition-all"
                placeholder="Caption or artwork title..."
              />
            </div>

            {/* Video Link (If Video Type) */}
            {editMediaType === "video" && (
              <div>
                <label className="block text-xs font-semibold text-[#C8A165] mb-1.5">
                  YouTube Video Link
                </label>
                <input
                  type="text"
                  value={editMediaUrl}
                  onChange={(e) => setEditMediaUrl(e.target.value)}
                  className="w-full text-xs p-3 bg-[#18181D] text-[#F8F5F2] border border-[#C8A165]/25 rounded-2xl focus:outline-none focus:border-[#C8A165] transition-all"
                  placeholder="https://www.youtube.com/watch?v=..."
                />
              </div>
            )}

            <div className="pt-3 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditModalOpen(false)}
                className="px-4 py-2 rounded-xl border border-[#C8A165]/20 text-xs font-medium text-[#F8F5F2]/70 hover:bg-white/5 transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <motion.button
                type="submit"
                disabled={loading}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                className="px-5 py-2 rounded-xl text-xs font-bold text-[#0A0A0C] tracking-wider uppercase cursor-pointer"
                style={{
                  background: "linear-gradient(135deg, #C8A165 0%, #F5E5C9 50%, #9E7A48 100%)",
                }}
              >
                Save Changes
              </motion.button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {loading && uploadProgress ? (
        <Loading
          title={`Uploading (${uploadProgress.current} out of ${uploadProgress.total} completed)`}
          message={`Uploading image ${Math.min(uploadProgress.current + 1, uploadProgress.total)} of ${uploadProgress.total}. Please do not close this window...`}
        />
      ) : (loading || galleryLoading) ? (
        <Loading />
      ) : null}
    </div>
  );
}
